import { useEffect } from 'react';
import { useSearchParams } from 'react-router';
import { writeEmailAttribution } from '../util/emailAttributionCookie';

/**
 * Param names mirrored from `GetMatchedResponse::REF_PARAM` / `REF_SUBMISSION_PARAM`
 * (app/src/Lib/Email/Templates/GetMatchedResponse.php); both must move together.
 */
const REF_PARAM = 'ref';
const REF_SUBMISSION_PARAM = 'refSubmission';

/**
 * Recognised `ref` values mapped to the series key stamped into tracking events.
 * An unrecognised `ref` stores nothing and is left in the URL — it may belong to
 * something else.
 */
const REF_SOURCES: Record<string, string> = {
    'get-matched-response': 'GetMatchedResponse',
};

/**
 * Records the email link that brought the visitor here, when the landing URL
 * carries a recognised `ref` param.
 *
 * The params are read and left where they are. A page view fires on any change to
 * `location.search`, so rewriting the URL to drop them turns one landing into two
 * PageView events — one for the emailed URL and one for the cleaned one — which
 * splits the visit across two URLs in every report keyed on it. `useAdProfessional`
 * reads `utm_campaign` and leaves it in place for the same reason.
 *
 * The stored attribution is stamped into the `CreatedEnquiry` event's ExtraData
 * (`trackCreatedEnquiry`), which is how a Get Matched response email shows up as
 * an enquiry source in the QDD source split.
 *
 * Call this once, from the root component.
 */
export function useEmailAttribution() {
    const [searchParams] = useSearchParams();

    useEffect(() => {
        const ref = searchParams.get(REF_PARAM);
        const source = ref ? REF_SOURCES[ref] : undefined;
        if (!source) {
            return;
        }

        const submissionId = Number(searchParams.get(REF_SUBMISSION_PARAM));
        writeEmailAttribution({
            source,
            ...(Number.isInteger(submissionId) &&
                submissionId > 0 && { submissionId }),
        });
    }, [searchParams]);
}
